import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouteObject, RouterProvider } from 'react-router-dom'
import reportWebVitals from './reportWebVitals'
import NotFoundPage from './routes/404'
import Login from './routes/login'
import MSLoginPage from './routes/mslogin'
import NavLayout from './routes/NavLayout'
import ResetPassword from './routes/reset-password'
import Root from './routes/Root'
import theme from './util/theme'
import ErrorPage from './routes/Error'
import AutoLogin from './routes/auto-login'
import './util/tracking/posthog'
import ScrollToTop from './components/ScrollToTop'
import Configure from './routes/configure'
import AlertPage from './routes/alerts'
import OverallStatsPage from './routes/dashboards/overall-stats'
import AllAlertsPage from './routes/dashboards/all-alerts'
import NewAuditConfigPage from './routes/configure/new'
import EditAuditConfigPage from './routes/configure/[auditConfigId]'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './util/queryClient'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ErrorBoundary } from 'react-error-boundary'
import RequireDirectoryAdmin from './routes/RequireDirectoryAdmin'
import RequireDirectory from './routes/RequireDirectory'

const wrapRoutesWithErrorBoundary = (routes: RouteObject[]) => {
  return routes.map((p) => ({
    ...p,
    errorElement: <ErrorPage />,
  }))
}

const router = createBrowserRouter([
  {
    errorElement: <ErrorPage />,
    element: (
      <>
        <ScrollToTop />
        <NavLayout />
      </>
    ),
    children: wrapRoutesWithErrorBoundary([
      {
        path: '/auto-login',
        element: <AutoLogin />,
      },
      {
        path: '/',
        element: <Root />,
      },
      {
        path: '/login',
        element: <Login />,
      },
      {
        path: '/reset-password',
        element: <ResetPassword />,
      },
      {
        path: '/mslogin',
        element: <MSLoginPage />,
      },
      {
        element: <RequireDirectory />,
        children: wrapRoutesWithErrorBoundary([
          {
            path: '/alerts',
            element: <AlertPage />,
          },
          {
            path: '/dashboards/overall-stats',
            element: <OverallStatsPage />,
          },
          {
            path: '/dashboards/all-alerts',
            element: <AllAlertsPage />,
          },
          {
            element: <RequireDirectoryAdmin />,
            children: wrapRoutesWithErrorBoundary([
              {
                path: '/configure',
                element: <Configure />,
              },
              {
                path: '/configure/:auditConfigId',
                element: (
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <EditAuditConfigPage />
                  </React.Suspense>
                ),
              },
              {
                path: '/configure/new',
                element: <NewAuditConfigPage />,
              },
            ]),
          },
        ]),
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ]),
  },
])

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <React.StrictMode>
    <ErrorBoundary FallbackComponent={ErrorPage}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <RouterProvider router={router} />
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
